import { useState } from 'react';
import { TableRow, TableCell, IconButton } from '@material-ui/core';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';

import { useStoreActions } from '~/store/hooks';
import { isoToFormat } from '~/utils/date';
import { extractErrorMessage } from '~/utils/error';
import { getEditValues } from '../form';
import ConfirmModal from '~/ui/components/common/ConfirmModal';
import InterventionModal from '../../popups/InterventionModal';

import { Timezone } from '~/types';
import { ICarePlanIntervention } from '~/services/api/carePlanInterventions/types';
import { IFormValues } from '../../popups/InterventionModal/form/types';
import { IPagePermissions } from '../../../types';
import styles from './Item.module.scss';
import ReadMoreLessHtml from '~/ui/components/common/ReadMoreLessHtml';

interface IProps {
  item: ICarePlanIntervention;
  permissions: IPagePermissions;
  onCarePlanChange: () => void;
}

const Item = ({ item, permissions, onCarePlanChange }: IProps) => {
  const [showEdit, setShowEdit] = useState(false);
  const [showRemove, setShowRemove] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const { onUpdateCarePlanIntervention, onRemoveCarePlanIntervention } = useStoreActions(
    actions => actions.carePlanInterventions,
  );
  const { showError } = useStoreActions(actions => actions.snackbar);

  const handleUpdateIntervention = async (values: IFormValues) => {
    const payload = {
      ...values,
      id: item.id,
      date: isoToFormat(values.date, 'y-MM-dd'),
    };

    try {
      setIsUpdating(true);
      await onUpdateCarePlanIntervention(payload);
      onCarePlanChange();
      setShowEdit(false);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveIntervention = async () => {
    try {
      setIsRemoving(true);
      await onRemoveCarePlanIntervention(item.id);
      onCarePlanChange();
    } catch (e) {
      showError(extractErrorMessage(e));
      setIsRemoving(false);
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>{isoToFormat(item.date, 'dd MMM y', Timezone.GMT, false)}</TableCell>
        <TableCell>
          <ReadMoreLessHtml html={item.reasonHtml} />
        </TableCell>
        {permissions.canManage && (
          <TableCell align="right">
            <IconButton
              className={styles.iconButton}
              title="Edit"
              onClick={() => setShowEdit(true)}
            >
              <CreateIcon className={styles.icon} />
            </IconButton>
            <IconButton
              className={styles.iconButton}
              title="Remove"
              onClick={() => setShowRemove(true)}
            >
              <DeleteIcon className={styles.icon} />
            </IconButton>
          </TableCell>
        )}
      </TableRow>
      {showEdit && (
        <InterventionModal
          mode="edit"
          initialValues={getEditValues(item)}
          isProcessing={isUpdating}
          onSubmit={handleUpdateIntervention}
          onClose={() => setShowEdit(false)}
        />
      )}
      {showRemove && (
        <ConfirmModal
          isLoading={isRemoving}
          title="Remove Intervention"
          description="Are you sure you want to remove current Intervention?"
          onClose={() => setShowRemove(false)}
          onConfirm={handleRemoveIntervention}
        />
      )}
    </>
  );
};

export default Item;
